import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import {
  color,
  space,
  layout,
  flexbox,
  SpaceProps,
  ColorProps,
  LayoutProps,
  FlexboxProps,
} from 'styled-system';
import Loading from '../Loading';

const buttonShadow = '0 4px 6px rgba(50, 50, 93, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';

export interface ButtonProps extends React.ComponentProps<typeof StyledButton> {
  loading?: boolean;
  children?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { loading, children, ...rest } = props;
  const [loadingVisible, setLoadingVisible] = useState(false);

  /**
   * Flow to get the width animation:
   * 1. New prop loading comes in and switches from display: none to inline-block
   * 2. Internal state updated after render to increase width and margin from 0
   */
  useEffect(() => {
    setLoadingVisible(Boolean(loading));
  }, [loading]);

  const buttonProps: React.ComponentProps<typeof StyledButton> = {
    disabled: loading,
    ...rest,
  };

  const spinnerSize = 13;

  const loadingProps = {
    size: spinnerSize,
    color: '#fff',
    style: {
      marginLeft: loadingVisible ? '10px' : '0',
      width: loadingVisible ? `${spinnerSize}px` : '0px',
      display: loading ? 'inline-block' : 'none',
      transition: 'width 0.3s, margin 0.3s',
      overflow: 'hidden',
    },
  };

  return (
    <StyledButton {...buttonProps} ref={ref}>
      {children}
      {<Loading {...loadingProps} />}
    </StyledButton>
  );
});

export enum ButtonSize {
  Small = 'small',
  Default = 'default',
  Big = 'big',
}

function isButtonSize(size: string): size is ButtonSize {
  return Object.values(ButtonSize).includes(size as ButtonSize);
}

const buttonFontSizes: Record<ButtonSize, string> = {
  [ButtonSize.Small]: '1.3rem',
  [ButtonSize.Default]: '1.6rem',
  [ButtonSize.Big]: '2rem',
};

function getButtonFontSize(size: string): string {
  if (isButtonSize(size)) {
    return buttonFontSizes[size];
  }
  return buttonFontSizes[ButtonSize.Default];
}

function getButtonPadding(size: string, variableWidth: boolean): string {
  if (size === ButtonSize.Small) {
    return '6px';
  }
  if (variableWidth) {
    if (size === ButtonSize.Big) return '10px 30px';
    return '6px 12px';
  }
  return '0';
}

type StyledButtonProps = SpaceProps &
  ColorProps &
  LayoutProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  FlexboxProps & {
    size?: ButtonSize;
    variableWidth?: boolean;
    mobile?: boolean;
    disabledColor?: string;
    highlighted?: boolean;
    look?: 'primary' | 'secondary';
  };

export const StyledButton = styled.button<StyledButtonProps>`
  display: ${(props) => (props.mobile ? 'none' : 'initial')};
  font-family: ${(props) => props.theme.fontButton};
  font-size: ${(props) => getButtonFontSize(String(props.size))};
  line-height: ${(props) =>
    props.size === 'small' ? '1.5rem' : props.variableWidth ? '23px' : '35px'};
  color: ${(props) =>
    props.highlighted
      ? props.theme.colors.buttons.highlighted.textColor
      : props.look === 'secondary'
      ? props.theme.colors.buttons.secondary.textColor
      : props.theme.colors.buttons.primary.textColor};
  background-color: ${(props) =>
    props.highlighted
      ? props.theme.colors.buttons.highlighted.backgroundColor
      : props.look === 'secondary'
      ? props.theme.colors.buttons.secondary.backgroundColor
      : props.theme.colors.buttons.primary.backgroundColor};
  border: none;
  border-radius: ${(props) => props.theme.buttons.borderRadius};
  height: ${(props) => (props.variableWidth ? 'fit-content' : '35px')};
  width: ${(props) => (props.variableWidth ? 'fit-content' : '145px')};
  max-width: 100%;
  padding: ${(props) =>
    getButtonPadding(String(props.size), Boolean(props.variableWidth))};
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  ${space}
  ${color}
  ${layout}
  ${flexbox}

  @media screen and (max-width: 767px) {
    display: initial;
  }

  :hover {
    transform: translateY(-1px);
    box-shadow: ${buttonShadow};
  }

  :active {
    transform: translateY(1px);
  }

  :disabled {
    background-color: ${(props) =>
      props.highlighted
        ? props.theme.colors.buttons.highlighted.backgroundColor
        : props.disabledColor
        ? props.disabledColor
        : props.theme.colors.buttons.disabled.backgroundColor};
    color: ${(props) => props.theme.colors.buttons.disabled.textColor};
    cursor: not-allowed;
  }

  :disabled:hover {
    transform: none;
    box-shadow: none;
  }

  /* Fake disabled, looks disabled but can be clicked */
  &.disabled {
    background-color: ${(props) =>
      props.highlighted
        ? props.theme.colors.buttons.highlighted.backgroundColor
        : props.disabledColor
        ? props.disabledColor
        : props.theme.colors.buttons.disabled.backgroundColor};
    color: ${(props) => props.theme.colors.buttons.disabled.textColor};
    cursor: default;
  }

  &.disabled:hover {
    transform: none;
    box-shadow: none;
  }

  &.focus-visible {
    outline: none;
    box-shadow: 0px 0px 0px 2px ${(props) => props.theme.highLightColor};
  }
`;
